/**
 * DAMAssetPicker Component
 * Modal dialog to browse and select existing DAM assets
 * Features:
 * - Grid view of images
 * - Search/filter functionality
 * - Thumbnail previews
 * - Select button to choose asset
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { cn } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Search,
  Image as ImageIcon,
  Check,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
} from 'lucide-react'
import { assetService } from '@/lib/api/services/assets'

// ============================================
// Types
// ============================================

export interface DAMAssetPickerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelect: (asset: {
    id: string
    url: string
    thumbnailUrl?: string
    name: string
    width?: number
    height?: number
    fileSize: number
    mimeType: string
  }) => void
}

interface MockAsset {
  id: string
  name: string
  url: string
  thumbnailUrl: string
  type: string
  size: number
  width: number
  height: number
  mimeType: string
  createdAt: string
}

// ============================================
// Mock Data for Development
// ============================================

const MOCK_IMAGES: MockAsset[] = [
  {
    id: 'img-1',
    name: 'Company Logo.png',
    url: 'https://images.unsplash.com/photo-1549924231-f129b911e442?w=800',
    thumbnailUrl: 'https://images.unsplash.com/photo-1549924231-f129b911e442?w=200',
    type: 'Image',
    size: 245678,
    width: 800,
    height: 600,
    mimeType: 'image/png',
    createdAt: '2026-01-15T10:30:00Z',
  },
  {
    id: 'img-2',
    name: 'Team Photo.jpg',
    url: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800',
    thumbnailUrl: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=200',
    type: 'Image',
    size: 567890,
    width: 1200,
    height: 800,
    mimeType: 'image/jpeg',
    createdAt: '2026-01-14T14:20:00Z',
  },
  {
    id: 'img-3',
    name: 'Product Screenshot.png',
    url: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800',
    thumbnailUrl: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=200',
    type: 'Image',
    size: 389000,
    width: 1920,
    height: 1080,
    mimeType: 'image/png',
    createdAt: '2026-01-13T09:15:00Z',
  },
  {
    id: 'img-4',
    name: 'Office Building.jpg',
    url: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800',
    thumbnailUrl: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=200',
    type: 'Image',
    size: 892345,
    width: 1600,
    height: 1200,
    mimeType: 'image/jpeg',
    createdAt: '2026-01-12T16:45:00Z',
  },
  {
    id: 'img-5',
    name: 'Annual Report Cover.png',
    url: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800',
    thumbnailUrl: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=200',
    type: 'Image',
    size: 456123,
    width: 800,
    height: 1200,
    mimeType: 'image/png',
    createdAt: '2026-01-11T11:30:00Z',
  },
  {
    id: 'img-6',
    name: 'Conference Presentation.jpg',
    url: 'https://images.unsplash.com/photo-1515187029135-18ee286d815b?w=800',
    thumbnailUrl: 'https://images.unsplash.com/photo-1515187029135-18ee286d815b?w=200',
    type: 'Image',
    size: 678234,
    width: 1920,
    height: 1080,
    mimeType: 'image/jpeg',
    createdAt: '2026-01-10T13:00:00Z',
  },
  {
    id: 'img-7',
    name: 'Brand Guidelines Hero.png',
    url: 'https://images.unsplash.com/photo-1558655146-9f40138edfeb?w=800',
    thumbnailUrl: 'https://images.unsplash.com/photo-1558655146-9f40138edfeb?w=200',
    type: 'Image',
    size: 523456,
    width: 2400,
    height: 1600,
    mimeType: 'image/png',
    createdAt: '2026-01-09T08:20:00Z',
  },
  {
    id: 'img-8',
    name: 'Customer Success Story.jpg',
    url: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=800',
    thumbnailUrl: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=200',
    type: 'Image',
    size: 734567,
    width: 1280,
    height: 720,
    mimeType: 'image/jpeg',
    createdAt: '2026-01-08T15:10:00Z',
  },
  {
    id: 'img-9',
    name: 'Newsletter Header.png',
    url: 'https://images.unsplash.com/photo-1557426272-fc759fdf7a8d?w=800',
    thumbnailUrl: 'https://images.unsplash.com/photo-1557426272-fc759fdf7a8d?w=200',
    type: 'Image',
    size: 312890,
    width: 600,
    height: 200,
    mimeType: 'image/png',
    createdAt: '2026-01-07T12:40:00Z',
  },
  {
    id: 'img-10',
    name: 'Social Media Banner.jpg',
    url: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=800',
    thumbnailUrl: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=200',
    type: 'Image',
    size: 423789,
    width: 1200,
    height: 630,
    mimeType: 'image/jpeg',
    createdAt: '2026-01-06T10:05:00Z',
  },
  {
    id: 'img-11',
    name: 'Product Launch Banner.png',
    url: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800',
    thumbnailUrl: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=200',
    type: 'Image',
    size: 567890,
    width: 1920,
    height: 600,
    mimeType: 'image/png',
    createdAt: '2026-01-05T09:00:00Z',
  },
  {
    id: 'img-12',
    name: 'Employee Spotlight.jpg',
    url: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=800',
    thumbnailUrl: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=200',
    type: 'Image',
    size: 456234,
    width: 800,
    height: 1000,
    mimeType: 'image/jpeg',
    createdAt: '2026-01-04T14:30:00Z',
  },
]

const SORT_OPTIONS = [
  { label: 'Newest First', value: 'createdAt-desc' },
  { label: 'Oldest First', value: 'createdAt-asc' },
  { label: 'Name (A-Z)', value: 'name-asc' },
  { label: 'Name (Z-A)', value: 'name-desc' },
  { label: 'Size (Smallest)', value: 'size-asc' },
  { label: 'Size (Largest)', value: 'size-desc' },
]

const PAGE_SIZE = 8

// ============================================
// DAMAssetPicker Component
// ============================================

export function DAMAssetPicker({
  open,
  onOpenChange,
  onSelect,
}: DAMAssetPickerProps) {
  const [assets, setAssets] = useState<MockAsset[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState('createdAt-desc')
  const [page, setPage] = useState(1)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  // Load assets (using mock data for now)
  const loadAssets = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500))

      // Try to load real assets first
      try {
        const response = await assetService.getAssets({
          top: 100,
          filter: [{ field: 'type', operator: 'eq', value: 'Image' }],
        })

        if (response.value && response.value.length > 0) {
          // Map real assets to our format
          const realAssets = response.value.map(asset => ({
            id: asset.id,
            name: asset.name,
            url: asset.url,
            thumbnailUrl: asset.thumbnailUrl || asset.url,
            type: asset.type,
            size: asset.size,
            width: 0, // Real API may not have these
            height: 0,
            mimeType: 'image/jpeg', // Default
            createdAt: asset.createdAt,
          }))
          setAssets(realAssets)
          return
        }
      } catch {
        // API not available, use mock data
      }

      // Fall back to mock data
      setAssets(MOCK_IMAGES)
    } catch (err) {
      setError('Failed to load images')
    } finally {
      setLoading(false)
    }
  }, [])

  // Load assets when dialog opens
  useEffect(() => {
    if (open) {
      loadAssets()
      setSelectedId(null)
      setSearch('')
      setPage(1)
    }
  }, [open, loadAssets])

  // Filter and sort assets
  const filteredAssets = useMemo(() => {
    let result = [...assets]

    // Filter by search
    if (search) {
      const searchLower = search.toLowerCase()
      result = result.filter(asset =>
        asset.name.toLowerCase().includes(searchLower)
      )
    }

    // Sort
    const [sortField, sortDir] = sortBy.split('-')
    result.sort((a, b) => {
      let comparison = 0
      if (sortField === 'name') {
        comparison = a.name.localeCompare(b.name)
      } else if (sortField === 'size') {
        comparison = a.size - b.size
      } else if (sortField === 'createdAt') {
        comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      }
      return sortDir === 'desc' ? -comparison : comparison
    })

    return result
  }, [assets, search, sortBy])

  // Pagination
  const totalPages = Math.ceil(filteredAssets.length / PAGE_SIZE)
  const paginatedAssets = filteredAssets.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  )

  // Handle select
  const handleSelect = useCallback(() => {
    const asset = assets.find(a => a.id === selectedId)
    if (asset) {
      onSelect({
        id: asset.id,
        url: asset.url,
        thumbnailUrl: asset.thumbnailUrl,
        name: asset.name,
        width: asset.width,
        height: asset.height,
        fileSize: asset.size,
        mimeType: asset.mimeType,
      })
    }
  }, [selectedId, assets, onSelect])

  // Format file size
  const formatFileSize = (bytes: number): string => {
    return assetService.formatFileSize(bytes)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5 text-purple-500" />
            Select Image from Library
          </DialogTitle>
          <DialogDescription>
            Browse and select an image from your digital asset library
          </DialogDescription>
        </DialogHeader>

        {/* Search and Sort */}
        <div className="flex flex-col sm:flex-row gap-3 py-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
            <Input
              type="text"
              placeholder="Search images..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
              className="pl-10 rounded-xl"
            />
          </div>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[180px] rounded-xl">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Asset Grid */}
        <ScrollArea className="flex-1 min-h-0">
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 p-1">
              {Array.from({ length: PAGE_SIZE }).map((_, i) => (
                <Skeleton key={i} className="aspect-square rounded-xl" />
              ))}
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="h-12 w-12 text-red-400 mb-4" />
              <p className="text-neutral-500 dark:text-neutral-400">{error}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={loadAssets}
                className="mt-4 rounded-lg"
              >
                Try Again
              </Button>
            </div>
          ) : paginatedAssets.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <ImageIcon className="h-12 w-12 text-neutral-300 dark:text-neutral-600 mb-4" />
              <p className="text-neutral-500 dark:text-neutral-400">
                {search ? 'No images match your search' : 'No images found'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 p-1">
              {paginatedAssets.map((asset) => (
                <button
                  key={asset.id}
                  type="button"
                  onClick={() => setSelectedId(asset.id)}
                  className={cn(
                    'group relative aspect-square rounded-xl overflow-hidden',
                    'border-2 transition-all',
                    'focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2',
                    selectedId === asset.id
                      ? 'border-purple-500 ring-2 ring-purple-500 ring-offset-2'
                      : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600'
                  )}
                >
                  <img
                    src={asset.thumbnailUrl}
                    alt={asset.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => {
                      const target = e.currentTarget
                      target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23f3f4f6" width="100" height="100"/%3E%3C/svg%3E'
                    }}
                  />

                  {/* Selection Indicator */}
                  {selectedId === asset.id && (
                    <div className="absolute top-2 right-2 bg-purple-500 rounded-full p-1">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                  )}

                  {/* Hover Info */}
                  <div
                    className={cn(
                      'absolute inset-x-0 bottom-0 p-2',
                      'bg-gradient-to-t from-black/70 to-transparent',
                      'transform transition-transform',
                      'translate-y-full group-hover:translate-y-0'
                    )}
                  >
                    <p className="text-white text-xs font-medium truncate">
                      {asset.name}
                    </p>
                    <p className="text-white/70 text-xs">
                      {formatFileSize(asset.size)}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-4 border-t border-neutral-200 dark:border-neutral-700">
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              Page {page} of {totalPages} ({filteredAssets.length} images)
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p - 1)}
                disabled={page <= 1}
                className="rounded-lg"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={page >= totalPages}
                className="rounded-lg"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Footer */}
        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-xl"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSelect}
            disabled={!selectedId}
            className="rounded-xl"
          >
            <Check className="h-4 w-4 mr-1.5" />
            Select Image
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default DAMAssetPicker
